<script lang="ts" setup>
    import { reactive, watch, ref } from 'vue';

    import WindowSearchBase from '../../components/Search/WindowSearchBase.vue'
    import { patient } from '../../lib/ChoiceApi';
    import { WindowSearch } from '../../lib/ChoiceApi';
    import { WindowSearchPatientInputs, WindowSearchPatientListItem, WindowSearchColumnNameItem, WindowSearchAdditionalPlacement, WindowSearchDropdownAlignment } from '../../lib/Api/WindowSearch';
    import type { ComponentPublicInstance } from 'vue';

    const searchPatientRef = ref<ComponentPublicInstance | null>(null);

    const emit = defineEmits(['update:modelValue']);

    defineExpose({
        focus: () => (searchPatientRef.value as any)?.focus()
    });

    const props = withDefaults(defineProps<{
        modelValue: WindowSearchPatientListItem | null
        newLink?: boolean,
        newLinkTooltip?: string,
        disabled?: boolean,
        placeholder?: string,
        label?: string,
        additionalPlacement?: WindowSearchAdditionalPlacement,
        fixedSearchWidth?: number,
        dropdownWidth?: number,
        dropdownPlacement?: WindowSearchDropdownAlignment,
        addPatientTouch?: boolean
    }>(), {
        newLink: false,
        newLinkTooltip: "Create a new Rendering Provider",
        disabled: false,
        additionalPlacement: WindowSearchAdditionalPlacement.Top,
        dropdownPlacement: WindowSearchDropdownAlignment.Left,
        addPatientTouch: true
    });

    const columnNames = ref<WindowSearchColumnNameItem[]>([
        { columnName: "patientId", tooltip: "Chart #", width: "60px" },
        { columnName: "patientName", tooltip: "Patient Name" },
        { columnName: "gender", tooltip: "Gender", width: "60px" },
        { columnName: "birthDate", tooltip: "Birth Date", width: "70px" },
        { columnName: "age", tooltip: "Age", width: "30px" }
    ]);

    const formData = reactive<{
        searchResults: WindowSearchPatientListItem[],
        searchValue: WindowSearchPatientListItem | null,
        totalRows: number,
        maxReturnRows: number,
        disabled?: boolean
    }>({
        searchResults: [],
        searchValue: null,     
        totalRows: 0,
        maxReturnRows: 0,
        disabled: props.disabled || false    
    });

    watch(() => props.modelValue, (_new, _old) => {
        if (_new !== _old) {
            formData.searchValue = _new;
        }   
    });

    watch(() => props.disabled, (_new, _old) => {
        if (_new !== _old) {
            formData.disabled = _new;
        }   
    });

    async function onSearch(searchText: string) { 
        const inputs: WindowSearchPatientInputs = {
            searchText
        };
        const respSearch = await WindowSearch.Patient(inputs);
        formData.searchResults = respSearch?.searchList || [];
        formData.totalRows = respSearch?.totalRows || 0;
        formData.maxReturnRows = respSearch?.maxReturnRows || 0;
    }

    function onNew() {
        alert("New needs to be built");
    }

    async function onUpdateModelValue(value: WindowSearchPatientListItem) {
        emit('update:modelValue', value);
        if (props.addPatientTouch && value?.patientId != null) {
            await patient.addPatientTouchByPatientId(value.patientId);
        }
    }

    function onReset() {
        formData.searchResults = [];
        formData.totalRows = 0;
        formData.maxReturnRows = 0;
    }
</script>

<template>
    <WindowSearchBase
        ref="searchPatientRef"
        v-model="formData.searchValue" 
        :columns="columnNames"
        selectedColumn="patientName"
        :searchResults="formData.searchResults"
        :totalRows="formData.totalRows"
        :maxReturnRows="formData.maxReturnRows"
        :newLink="props.newLink"
        :disabled="formData.disabled"
        :placeholder="props.placeholder"
        :label="props.label"
        :additionalPlacement="props.additionalPlacement"
        :fixedSearchWidth="props.fixedSearchWidth"
        :dropdownWidth="props.dropdownWidth"
        :dropdownPlacement="props.dropdownPlacement"
        @onSearch="onSearch"
        @onNew="onNew"
        @update:modelValue="onUpdateModelValue"
        @onReset="onReset"
        />
</template>
